/**
 * Resolve a university media path to a URL the browser can actually load.
 *
 * The search, options and detail APIs return each image as EITHER an absolute
 * URL (when the API host has REFERENCE_ASSET_BASE_URL set) OR the bare relative
 * path MongoDB stores, e.g. `catalog/University_Favicon/x.png` (when it does
 * not — which is the case on the dev box today). A relative path left as-is
 * resolves against the SPA origin, where the static server rewrites every
 * non-`/api` path to `index.html`; the <img> then loads HTML, fails to decode,
 * and every card drops to its initials monogram. That is the "images work on
 * localhost but not on the server" bug.
 *
 * `assetUrl` is the frontend's backstop: an already-absolute URL (`https://…`,
 * protocol-relative `//…`) or a `data:` URI is returned untouched; anything
 * else is joined onto ASSET_BASE so it loads no matter how the API host is
 * configured. Point `VITE_ASSET_BASE_URL` elsewhere at build time to override,
 * or set it empty to disable joining (relative paths then pass through).
 */

// The reference product's asset origin — the same value course-finder-be uses
// for REFERENCE_ASSET_BASE_URL. Kept as the default so images render even when
// no build-time override is supplied.
const DEFAULT_ASSET_BASE = 'https://crizac-assets.s3.ap-south-1.amazonaws.com/system';

const OVERRIDE = typeof import.meta !== 'undefined' && import.meta.env
  ? import.meta.env.VITE_ASSET_BASE_URL
  : undefined;

const ASSET_BASE = String(OVERRIDE ?? DEFAULT_ASSET_BASE).replace(/\/+$/, '');

export function assetUrl(path) {
  if (path == null) return null;
  const s = String(path).trim();
  if (!s) return null;
  // Absolute (`https://…`, `http://…`), protocol-relative (`//…`), or a data URI.
  if (/^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(s) || /^data:/i.test(s)) return s;
  if (!ASSET_BASE) return s;
  return `${ASSET_BASE}/${s.replace(/^\/+/, '')}`;
}
